import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { branding } from '@/config/branding';
import { queryClient } from './query-client';
import { homeRouteFor } from '@/domains/auth/permissions';
import { applyBrandTheme } from '@/lib/theme';
import { isOrganizationUsable, useActiveContext, usePreferences, useSession } from '@/stores/session-store';
import { AppLayout } from '@/layouts/AppLayout';
import { ErrorBoundary } from '@/components/error-boundary';
import { ToastProvider } from '@/components/ui/toast';
import { UpdateBanner } from '@/components/update-banner';
import { Button, LoadingState } from '@/components/ui/primitives';
import { BrandMark } from '@/components/brand-mark';
import { LoginPage } from '@/routes/LoginPage';

// Le schermate operative sono caricate su richiesta: chi lavora in cucina non
// scarica il codice dei report, e viceversa.
const DashboardPage = lazy(() => import('@/routes/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const FloorPage = lazy(() => import('@/routes/FloorPage').then((m) => ({ default: m.FloorPage })));
const OrdersPage = lazy(() => import('@/routes/OrdersPage').then((m) => ({ default: m.OrdersPage })));
const OrderPage = lazy(() => import('@/routes/OrderPage').then((m) => ({ default: m.OrderPage })));
const KitchenPage = lazy(() => import('@/routes/KitchenPage').then((m) => ({ default: m.KitchenPage })));
const CashPage = lazy(() => import('@/routes/CashPage').then((m) => ({ default: m.CashPage })));
const ReservationsPage = lazy(() =>
  import('@/routes/ReservationsPage').then((m) => ({ default: m.ReservationsPage })),
);
const CustomersPage = lazy(() => import('@/routes/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const MenuPage = lazy(() => import('@/routes/MenuPage').then((m) => ({ default: m.MenuPage })));
const InventoryPage = lazy(() => import('@/routes/InventoryPage').then((m) => ({ default: m.InventoryPage })));
const ReportsPage = lazy(() => import('@/routes/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const SettingsPage = lazy(() => import('@/routes/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const StaffPage = lazy(() => import('@/routes/StaffPage').then((m) => ({ default: m.StaffPage })));
const PlatformPage = lazy(() => import('@/routes/PlatformPage').then((m) => ({ default: m.PlatformPage })));
const PublicPortalPage = lazy(() =>
  import('@/routes/PublicPortalPage').then((m) => ({ default: m.PublicPortalPage })),
);

/** Blocca l'accesso alle rotte operative finché la sessione non è verificata. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const status = useSession((state) => state.status);
  const contexts = useSession((state) => state.contexts);
  const user = useSession((state) => state.user);
  const isPlatformAdmin = useSession((state) => state.isPlatformAdmin);
  const activeContext = useActiveContext();
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <LoadingState label="Verifica della sessione…" />
      </div>
    );
  }

  if (status === 'anonymous') {
    // Query e frammento seguono il redirect: se contengono l'esito di un
    // accesso con Google non riuscito, la schermata di accesso lo mostra.
    return (
      <Navigate
        to={{ pathname: '/accedi', search: location.search, hash: location.hash }}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  if (status === 'error') {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <BrandMark />
        <p className="max-w-md text-sm text-muted-foreground">
          Non è stato possibile caricare la sessione. Verifica la connessione e riprova.
        </p>
        <Button onClick={() => window.location.reload()}>Ricarica</Button>
      </div>
    );
  }

  if (contexts.length === 0 && isPlatformAdmin) {
    // Chi amministra la piattaforma senza essere in un ristorante lavora dalla console.
    return <Navigate to="/piattaforma" replace />;
  }

  if (activeContext && !isOrganizationUsable(activeContext)) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <BrandMark showTagline />
        <div className="max-w-md space-y-2">
          <h1 className="text-lg font-semibold">{activeContext.organization_name} è sospeso</h1>
          <p className="text-sm text-muted-foreground">
            L’accesso a questo ristorante è stato sospeso. I dati sono conservati e tornano disponibili alla
            riattivazione. Per informazioni scrivi a{' '}
            <a className="underline" href={`mailto:${branding.company.supportEmail}`}>
              {branding.company.supportEmail}
            </a>
            .
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {isPlatformAdmin ? (
            <Button asChild>
              <a href="/piattaforma">Console della piattaforma</a>
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => void useSession.getState().signOut()}>
            Esci
          </Button>
        </div>
      </div>
    );
  }

  if (contexts.length === 0) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <BrandMark showTagline />
        <div className="max-w-md space-y-2">
          <h1 className="text-lg font-semibold">Nessuna organizzazione associata</h1>
          <p className="text-sm text-muted-foreground">
            Il tuo account{user?.email ? <> (<strong>{user.email}</strong>)</> : null} non appartiene
            ancora a un ristorante. Chiedi al titolare di invitare questo indirizzo da Impostazioni →
            Persone, poi esci e rientra. Per assistenza scrivi a{' '}
            <a className="underline" href={`mailto:${branding.company.supportEmail}`}>
              {branding.company.supportEmail}
            </a>
            .
          </p>
        </div>
        <Button variant="outline" onClick={() => void useSession.getState().signOut()}>
          Esci
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Inoltra la radice all'app **conservando query e frammento**: al ritorno da
 * Google Supabase può atterrare qui con `?code=…`, e perderlo nel redirect
 * annullerebbe l'accesso prima che il client lo scambi con una sessione.
 */
function RootRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/app', search: location.search, hash: location.hash }} replace />;
}

/** La console della piattaforma è riservata a chi è in `platform_admins`; il database lo verifica comunque. */
function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const status = useSession((state) => state.status);
  const isPlatformAdmin = useSession((state) => state.isPlatformAdmin);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <LoadingState label="Verifica della sessione…" />
      </div>
    );
  }
  if (status !== 'authenticated') return <Navigate to="/accedi" replace />;
  if (!isPlatformAdmin) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

/** Reindirizza ciascun ruolo alla schermata in cui lavora abitualmente. */
function RoleHomeRedirect() {
  const context = useActiveContext();
  const target = context ? homeRouteFor(context.role_code, context.permissions) : '/app/dashboard';
  return <Navigate to={target} replace />;
}

function NotFoundPage() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <BrandMark />
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Pagina non trovata</h1>
        <p className="text-sm text-muted-foreground">L’indirizzo richiesto non esiste.</p>
      </div>
      <Button asChild>
        <a href="/app">Torna all’applicazione</a>
      </Button>
    </div>
  );
}

export function App() {
  const initialize = useSession((state) => state.initialize);
  const theme = usePreferences((state) => state.theme);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  // Il tema applica i colori del branding centralizzato e segue la preferenza
  // di sistema quando l'utente non ne ha scelta una esplicita.
  useEffect(() => applyBrandTheme(theme), [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ErrorBoundary scope="root">
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/accedi" element={<LoginPage />} />
            <Route
              path="/piattaforma"
              element={
                <RequirePlatformAdmin>
                  <Suspense fallback={<LoadingState />}>
                    <PlatformPage />
                  </Suspense>
                </RequirePlatformAdmin>
              }
            />

            {/* Portale pubblico: nessuna autenticazione richiesta. */}
            <Route path="/locale/:orgSlug/:locationSlug" element={<PublicPortalPage />} />

            <Route
              path="/app"
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            >
              <Route index element={<RoleHomeRedirect />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="sala" element={<FloorPage />} />
              <Route path="ordini" element={<OrdersPage />} />
              <Route path="ordini/:orderId" element={<OrderPage />} />
              <Route path="cucina" element={<KitchenPage />} />
              <Route path="cassa" element={<CashPage />} />
              <Route path="prenotazioni" element={<ReservationsPage />} />
              <Route path="clienti" element={<CustomersPage />} />
              <Route path="menu" element={<MenuPage />} />
              <Route path="magazzino" element={<InventoryPage />} />
              <Route path="personale" element={<StaffPage />} />
              <Route path="report" element={<ReportsPage />} />
              <Route path="impostazioni" element={<SettingsPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <UpdateBanner />
        </ErrorBoundary>
      </ToastProvider>
    </QueryClientProvider>
  );
}
